import { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL, useAuth } from "./AuthContext";

// The help center's report form and the API's category keys
export const TICKET_CATEGORIES = ["Portfolio", "Leaderboard", "Referrals", "KYC", "Withdrawals", "Other"];
const CATEGORY_KEYS = {
  Portfolio: "portfolio",
  Leaderboard: "leaderboard",
  Referrals: "referrals",
  KYC: "kyc",
  Withdrawals: "withdrawals",
  Other: "other",
};
const CATEGORY_NAMES = Object.fromEntries(Object.entries(CATEGORY_KEYS).map(([name, key]) => [key, name]));

const NETWORK_ERROR = "Couldn't reach the server. Check your connection and try again.";

// The team's queue states → the three the help center shows. A ticket reads as "Sent"
// until someone on the team has replied to it.
const portalStatus = (ticket) =>
  ticket.status === "resolved" ? "resolved" : ticket.status === "open" && !ticket.answered ? "sent" : "in_progress";

// API ticket → the shape the help center renders. `thread` is only on a full ticket.
const fromApi = (ticket) => ({
  id: ticket.id,
  issue: ticket.subject,
  category: CATEGORY_NAMES[ticket.category] ?? ticket.category,
  submittedAt: new Date(ticket.createdAt).getTime(),
  status: portalStatus(ticket),
  ...(ticket.thread ? { thread: ticket.thread } : {}),
});

// The signed-in trader's support tickets (GET /api/support/tickets), loaded once
// `enabled`. Writes resolve to { ticket } or { error }, so the modals can show the
// message inline. Attachments go straight to S3 with a presigned POST from the API.
export default function useSupportTickets(enabled) {
  const { token } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  const request = useCallback(
    async (path, { method = "GET", body, signal } = {}) => {
      let res;
      try {
        res = await fetch(`${API_URL}/api/support${path}`, {
          method,
          headers: { Authorization: `Bearer ${token}`, ...(body ? { "Content-Type": "application/json" } : {}) },
          body: body ? JSON.stringify(body) : undefined,
          signal,
        });
      } catch (err) {
        if (err.name === "AbortError") throw err;
        throw new Error(NETWORK_ERROR);
      }
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || "Something went wrong. Please try again.");
      return json;
    },
    [token]
  );

  const load = useCallback(
    (signal) => {
      setStatus((prev) => (prev === "ready" ? prev : "loading"));
      return request("/tickets", { signal })
        .then((body) => {
          setTickets(body.tickets.map(fromApi));
          setStatus("ready");
        })
        .catch((err) => {
          if (err.name === "AbortError") return;
          console.error("Failed to load support tickets:", err);
          setStatus((prev) => (prev === "ready" ? prev : "error"));
        });
    },
    [request]
  );

  useEffect(() => {
    if (!enabled || !token) return undefined;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [enabled, token, load]);

  // Presigned POST from the API, then the file straight to S3 → { key, name }
  const upload = useCallback(
    async (file) => {
      const { key, url, fields } = await request("/uploads", {
        method: "POST",
        body: { name: file.name, size: file.size, type: file.type },
      });
      const form = new FormData();
      Object.entries(fields).forEach(([field, value]) => form.append(field, value));
      form.append("file", file);
      let res;
      try {
        res = await fetch(url, { method: "POST", body: form });
      } catch {
        throw new Error(NETWORK_ERROR);
      }
      if (!res.ok) throw new Error("The screenshot didn't upload. Please try again.");
      return { key, name: file.name };
    },
    [request]
  );

  // Keeps the list in step with a ticket the API just returned
  const remember = useCallback((ticket) => {
    setTickets((prev) => [ticket, ...prev.filter((tk) => tk.id !== ticket.id)]);
    return ticket;
  }, []);

  const createTicket = useCallback(
    async ({ category, details, file }) => {
      try {
        const attachments = file ? [await upload(file)] : [];
        const { ticket } = await request("/tickets", {
          method: "POST",
          body: { category: CATEGORY_KEYS[category], body: details, attachments },
        });
        return { ticket: remember(fromApi(ticket)) };
      } catch (err) {
        return { error: err.message };
      }
    },
    [request, upload, remember]
  );

  // The full ticket with its conversation, for the details modal
  const loadTicket = useCallback(
    async (id) => {
      try {
        const { ticket } = await request(`/tickets/${encodeURIComponent(id)}`);
        return { ticket: fromApi(ticket) };
      } catch (err) {
        return { error: err.message };
      }
    },
    [request]
  );

  // Adding information to a resolved ticket reopens it (the API does that)
  const addUpdate = useCallback(
    async (id, { text, file }) => {
      try {
        const attachments = file ? [await upload(file)] : [];
        const { ticket } = await request(`/tickets/${encodeURIComponent(id)}/messages`, {
          method: "POST",
          body: { body: text, attachments },
        });
        const updated = fromApi(ticket);
        const { thread, ...row } = updated;
        setTickets((prev) => prev.map((tk) => (tk.id === id ? row : tk)));
        return { ticket: updated };
      } catch (err) {
        return { error: err.message };
      }
    },
    [request, upload]
  );

  const reload = useCallback(() => load(), [load]);

  return useMemo(
    () => ({ tickets, status, reload, createTicket, loadTicket, addUpdate }),
    [tickets, status, reload, createTicket, loadTicket, addUpdate]
  );
}
